import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRightIcon, ArrowDownIcon, MailIcon, BrainIcon, ListFilterIcon, SettingsIcon, BarChart3Icon, FileTextIcon, CheckIcon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { trackEvent as track } from '../../utils/analytics';
import { RequestsDashboardMock } from './RequestsDashboardMock';
import { RequestsPhoneMock } from './RequestsPhoneMock';
const EASE = [0.23, 1, 0.32, 1] as const;
type Bilingual = {
  en: string;
  fr: string;
};
const CAPABILITIES: {
  icon: typeof MailIcon;
  title: Bilingual;
  desc: Bilingual;
}[] = [{
  icon: MailIcon,
  title: {
    fr: 'Omnicanal',
    en: 'Omnichannel'
  },
  desc: {
    fr: 'Application, web, téléphone, courriel, réseaux sociaux',
    en: 'App, web, phone, email, social media'
  }
}, {
  icon: BrainIcon,
  title: {
    fr: 'Compréhension par l’IA',
    en: 'AI understanding'
  },
  desc: {
    fr: 'Analyse et contextualise',
    en: 'Analyzes and contextualizes'
  }
}, {
  icon: ListFilterIcon,
  title: {
    fr: 'Routage intelligent',
    en: 'Intelligent routing'
  },
  desc: {
    fr: 'Achemine vers la bonne équipe et les bons systèmes',
    en: 'Routes to the right team and systems'
  }
}, {
  icon: SettingsIcon,
  title: {
    fr: 'Suivi automatisé',
    en: 'Automated tracking'
  },
  desc: {
    fr: 'SLA, remontées hiérarchiques et notifications',
    en: 'SLAs, escalations and notifications'
  }
}, {
  icon: BarChart3Icon,
  title: {
    fr: 'Analytique en temps réel',
    en: 'Real-time analytics'
  },
  desc: {
    fr: 'Des décisions mieux informées',
    en: 'Better-informed decisions'
  }
}];
function AnalyticsCard({
  isFR
}: {
  isFR: boolean;
}) {
  const t = (fr: string, en: string) => isFR ? fr : en;
  const metrics = [{
    icon: <span className="w-8 h-8 rounded-full border border-[#C7CAF7] flex items-center justify-center" data-id="element-6370" data-mp="3ljq1yecthch">
          <FileTextIcon className="w-3.5 h-3.5 text-[#4F46E5]" strokeWidth={1.8} data-id="element-6371" data-mp="3uye2l8k9hilc" />
        </span>,
    value: '247',
    label: t('demandes', 'requests')
  }, {
    icon: <span className="w-8 h-8 rounded-full border-[3px] border-[#4F46E5] border-t-[#DADCF8]" data-id="element-6372" data-mp="1x3h2fpifd6w5" />,
    value: isFR ? '91 %' : '91%',
    label: t('dans les délais', 'on time')
  }, {
    icon: <span className="w-8 h-8 rounded-full border border-[#9ED9B4] flex items-center justify-center" data-id="element-6373" data-mp="1o9vna52clnm7">
          <ArrowDownIcon className="w-3.5 h-3.5 text-[#22A55B]" strokeWidth={2} data-id="element-6374" data-mp="19cv44py830zc" />
        </span>,
    value: isFR ? '−32 %' : '−32%',
    label: t('temps de traitement', 'processing time')
  }];
  return <div className="rounded-2xl border border-[#EEF0F4] bg-white px-5 py-4 shadow-[0_20px_50px_-24px_rgba(40,36,120,0.25)]" data-id="element-6375" data-mp="10esw3qg2n82k">
      <p className="font-sans text-[13px] font-medium text-[#121212]" data-id="element-6376" data-mp="15dvzz7kbf88y">{t('Aujourd’hui', 'Today')}</p>
      <div className="mt-3 grid grid-cols-3" data-id="element-6377" data-mp="2zq2eaf8ia55h">
        {metrics.map((m, i) => <div key={m.label} className={`px-3 ${i === 0 ? 'pl-0' : 'border-l border-[#EEF0F4]'}`} data-id="element-6378" data-mp="2znzpfyj3ai2c" data-mpi={i}>
            <span aria-hidden="true" className="block" data-id="element-6379" data-mp="39rwnrcatzcsn">{m.icon}</span>
            <p className="font-sans text-[19px] font-medium text-[#121212] mt-2.5 leading-none whitespace-nowrap" data-id="element-6380" data-mp="34w7h6p0e8vmu">{m.value}</p>
            <p className="font-sans text-[9.5px] text-[#5B6478] mt-1.5 leading-tight" data-id="element-6381" data-mp="3iofc0b84aciw">{m.label}</p>
          </div>)}
      </div>
    </div>;
}
function StatusCard({
  isFR
}: {
  isFR: boolean;
}) {
  const t = (fr: string, en: string) => isFR ? fr : en;
  const steps = [{
    title: t('Demande reçue', 'Request received'),
    sub: t('Aujourd’hui, 10 h 24', 'Today, 10:24 AM'),
    done: true
  }, {
    title: t('Équipe affectée', 'Team assigned'),
    sub: t('Travaux publics', 'Public works'),
    done: true
  }, {
    title: t('Citoyen informé', 'Resident informed'),
    sub: t('Notification envoyée', 'Notification sent'),
    done: true
  }, {
    title: t('SLA suivi', 'SLA tracked'),
    sub: t('Échéance : 48 h', 'Due: 48 h'),
    done: false
  }];
  return <ol className="rounded-2xl border border-[#EEF0F4] bg-white px-4 py-4 shadow-[0_20px_50px_-24px_rgba(40,36,120,0.25)]" data-id="element-6382" data-mp="24n98qnyqn6yt">
      {steps.map((s, i) => <li key={s.title} className="relative flex gap-3 pb-4 last:pb-0" data-id="element-6383" data-mp="29nx9408ux05p" data-mpi={i}>
          {i < steps.length - 1 && <span className="absolute left-[11px] top-7 bottom-1 w-[2px] bg-[#4F46E5]/70" aria-hidden="true" data-id="element-6384" data-mp="2dll8bfhl1f3e" />}
          {s.done ? <span className="relative w-6 h-6 shrink-0 rounded-full bg-[#4F46E5] flex items-center justify-center" aria-hidden="true" data-id="element-6385" data-mp="1lx9kg3aq1vwg">
              <CheckIcon className="w-3.5 h-3.5 text-white" strokeWidth={2.6} data-id="element-6386" data-mp="1pvquaulj1khk" />
            </span> : <span className="relative w-6 h-6 shrink-0 rounded-full border-2 border-dashed border-[#4F46E5]" aria-hidden="true" data-id="element-6387" data-mp="1uv3flawr2ji1" />}
          <span data-id="element-6388" data-mp="1290rvs59nnwf">
            <span className="block font-sans text-[11.5px] font-medium text-[#121212] leading-tight" data-id="element-6389" data-mp="127hec9azvqyc">{s.title}</span>
            <span className="block font-sans text-[10px] text-[#7A8194] mt-0.5" data-id="element-6390" data-mp="175iy6wg8vuav">{s.sub}</span>
          </span>
        </li>)}
    </ol>;
}
export function RequestsHero() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const pick = (v: Bilingual) => isFR ? v.fr : v.en;
  const t = (fr: string, en: string) => isFR ? fr : en;
  return <section className="relative overflow-hidden bg-white pt-14 pb-20 lg:pb-24 px-6" data-id="element-6391" data-mp="lothlphmbxt0">
      {/* soft lavender atmosphere behind the product */}
      <div className="pointer-events-none absolute right-[-10%] top-[8%] w-[60%] h-[85%] rounded-full bg-[#EEF0FF] opacity-70 blur-[110px]" aria-hidden="true" data-id="element-6392" data-mp="gr1paud2a9oa" />

      <div className="relative max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-[minmax(0,40fr)_minmax(0,60fr)] gap-14 lg:gap-8 items-center" data-id="element-6393" data-mp="6zf4c6ax8w8c">
        {/* LEFT — narrative */}
        <motion.div initial={{
        opacity: 0,
        y: 14
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease: EASE
      }} data-id="element-6394" data-mp="3u2jwen1fprpi">
          <p className="font-sans text-[13px] font-semibold tracking-[0.28em] uppercase text-[#4F46E5]" data-id="element-6395" data-mp="3fgmuu2o95ock">
            {t('Requêtes ATNA', 'ATNA Requests')}
          </p>
          <h1 className="font-serif text-[3.1rem] sm:text-[4rem] xl:text-[4.9rem] text-[#121212] leading-[0.98] tracking-tight mt-7" data-id="element-6396" data-mp="3jgeufpvo4k5s">
            {t('Chaque demande.', 'Every request.')}
            <span className="block" data-id="element-6397" data-mp="3aibi9k6kozqn">{t('Comprise.', 'Understood.')}</span>
            <span className="block" data-id="element-6398" data-mp="23ks9c5njooz">{t('Orchestrée.', 'Orchestrated.')}</span>
            <span className="block text-[#4F46E5]" data-id="element-6399" data-mp="71mbpy6zpq0z">{t('Résolue.', 'Resolved.')}</span>
          </h1>
          <p className="font-sans text-[18px] xl:text-[20px] text-[#5B6478] leading-[1.6] mt-8 max-w-[560px]" data-id="element-6400" data-mp="3oimbgtvcuqez">
            {t('Peu importe d’où elle vient, BCITI | ATNA comprend chaque demande, la priorise et orchestre son traitement jusqu’à sa résolution, dans l’ensemble de vos systèmes existants.', 'No matter where it comes from, BCITI | ATNA understands each request, prioritizes it and orchestrates its handling through resolution, across your existing systems.')}
          </p>

          <Link to="/request-demo" onClick={() => track('Demo CTA Clicked', {
          location: 'Requests Hero'
        })} className="mt-9 inline-flex items-center gap-3 rounded-full bg-[#4F46E5] px-8 py-4 font-sans text-[17px] text-white shadow-[0_14px_30px_-12px_rgba(79,70,229,0.6)] transition-colors duration-150 hover:bg-[#4338CA]" data-id="element-6401" data-mp="25in2i0cxky5a">
            {t('Voir Requêtes ATNA en action', 'See ATNA Requests in action')}
            <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} aria-hidden="true" data-id="element-6402" data-mp="20jtqydh7fh7r" />
          </Link>

          <ul className="mt-14 grid grid-cols-2 sm:grid-cols-5 gap-y-8" data-id="element-6403" data-mp="1vkh3bqiie644">
            {CAPABILITIES.map(({
            icon: Icon,
            title,
            desc
          }, i) => <li key={title.en} className={`flex flex-col items-center text-center px-2 ${i > 0 ? 'sm:border-l sm:border-[#E8E9EE]' : ''}`} data-id="element-6404" data-mp="3bxnr1kzmme1u" data-mpi={i}>
                <span className="w-12 h-12 rounded-full bg-[#F1F0FD] flex items-center justify-center" data-id="element-6405" data-mp="3r3jo1vc5xhir">
                  <Icon className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.6} aria-hidden="true" data-id="element-6406" data-mp="3w1uzgai3w033" />
                </span>
                <span className="font-sans text-[13px] font-medium text-[#121212] mt-3.5 leading-snug" data-id="element-6407" data-mp="3vg08k2eqzxv">{pick(title)}</span>
                <span className="font-sans text-[11px] text-[#7A8194] mt-1.5 leading-snug" data-id="element-6408" data-mp="dncbkwti8mk1">{pick(desc)}</span>
              </li>)}
          </ul>
        </motion.div>

        {/* RIGHT — layered product composition (desktop) */}
        <motion.div className="relative hidden lg:block h-[780px]" initial={{
        opacity: 0,
        y: 18
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease: EASE,
        delay: 0.08
      }} data-id="element-6409" data-mp="nml3lk4t7r3r">
          <div className="absolute left-[19%] right-[4%] top-[86px]" data-id="element-6410" data-mp="2pezjkdnsmlhj">
            <RequestsDashboardMock isFR={isFR} data-id="element-6411" data-mp="2tf9nlks0z1tr" />
          </div>
          <div className="absolute left-0 top-[200px] z-10" data-id="element-6412" data-mp="2lvikp2o8r30h">
            <RequestsPhoneMock isFR={isFR} data-id="element-6413" data-mp="2lx224wlmvk3u" />
          </div>
          <div className="absolute right-0 top-0 w-[340px] z-10" data-id="element-6414" data-mp="2h37i1s249348">
            <AnalyticsCard isFR={isFR} data-id="element-6415" data-mp="2c4w8zjjnawn2" />
          </div>
          <div className="absolute right-[-1%] bottom-[18px] w-[196px] z-10" data-id="element-6416" data-mp="2c5fhk9neo8of">
            <StatusCard isFR={isFR} data-id="element-6417" data-mp="284vkf62u152a" />
          </div>
        </motion.div>

        {/* RIGHT — stacked composition (tablet / mobile) */}
        <div className="lg:hidden space-y-6" data-id="element-6418" data-mp="34rqpk5dcdbv9">
          <div className="grid sm:grid-cols-[auto_minmax(0,1fr)] gap-6 items-start" data-id="element-6419" data-mp="2zui9cabr7hwn">
            <div className="mx-auto" data-id="element-6420" data-mp="2vsn7n4pwv2ic">
              <RequestsPhoneMock isFR={isFR} data-id="element-6421" data-mp="2vrljbyjvwjio" />
            </div>
            <div className="space-y-5" data-id="element-6422" data-mp="2qulh5yq5o0wu">
              <AnalyticsCard isFR={isFR} data-id="element-6423" data-mp="2qtsfvd9yu3m3" />
              <StatusCard isFR={isFR} data-id="element-6424" data-mp="1kdq7ayoa0qvo" />
            </div>
          </div>
          <RequestsDashboardMock isFR={isFR} data-id="element-6425" data-mp="1kbwxjaid9qjg" />
        </div>
      </div>
    </section>;
}